import React, { useState } from 'react';
import {
    Stack,
    Container,
    Grid,
    ButtonGroup,
    Button,
    InputLabel,
    MenuItem,
    Select,
    Box,
    FormControl,
    TextField,
    FormHelperText
} from '@mui/material';
import { FixNavbarSpace } from '../Utilities/Navbar';
import { SelectChangeEvent } from '@mui/material/Select';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { Navigate } from 'react-router-dom';
import Rectangle2CutCorners from './Rectangle2CutCorners';
import Rectangle4CutCorners from './Rectangle4CutCorners';
import OneCutCorner from './OneCutCorner';
import Octagon from './Octagon';
import Quadrilateral from './Quadrilateral';
import CircleShape from './CircleShape';
import QuadrilateralWithRoundCorners from './QuadrilateralWithRoundCorners';
import DealersService from '@api/Dealers/DealersCtrl';

const fetchAttributes = async () => {
    const attributes = await DealersService.getAttributes();
    console.log('Attributes:', attributes);
    return attributes;
  };

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

type InputValue = {
    'Order Type': string,
    'SO.': string,
    'PO.': string,
    'Company': string,
    'Tag Name': string,
    'Make': string,
    'Model': string,
    'Shape': string,
    'A': string,
    'B': string,
    'C': string,
    'D': string,
    'E': string,
    'F': string,
    'CUT SIDE': string,
    'TAPER': string,
    'Skirt': string,
    'Wrap': string,
    'Pillow': string,
    'Color': string,
    'Qty.': string,
    'Notes': string
};

const foldTypes = [
    { _id: 1, name: 'Bi-Fold', price: 0 },
    { _id: 2, name: 'Tri-Fold', price: 0 },
    { _id: 3, name: 'Quad-Fold', price: 0 },
    { _id: 4, name: 'Five-Fold', price: 0 },
    { _id: 5, name: 'Six-Fold', price: 0 }
];

const taperPrices: Record<string, number> = {
    '4-3': 550,
    '5-3': 575,
    '5-4': 600,
    '6-4': 630
};
const skirtLengthPrices = (value: string) => {
    const val = parseFloat(value);
    if (val >= 4.5 && val <= 6.5) return 15;
    if (val >= 7 && val <= 7.5) return 20;
    return 0;
};
const tieDownPrices = (value: string) => {
    if (value === '6') return 10;
    if (value === '8') return 20;
    return 0;
};




const StaticSpaShapes = [
    {
        _id: 'rectangleWith2CutCorners',
        name: 'Rectangle with 2 cut corners',
        dimensions: 6,
        items: foldTypes
    },
    {
        _id: 'rectangleWith4CutCorners',
        name: 'Rectangle with 4 cut corners',
        dimensions: 3,
        items: foldTypes
    },
    {
        _id: 'oneCutCorner',
        name: 'One cut corner',
        dimensions: 6,
        items: foldTypes
    },
    {
        _id: 'octagon',
        name: 'Octagon',
        dimensions: 1,
        items: foldTypes
    },
    {
        _id: 'quadrilateral',
        name: 'Rectangle/square with square corners',
        dimensions: 2,
        items: foldTypes
    },
    {
        _id: 'circle',
        name: 'Circle',
        dimensions: 1,
        items: foldTypes
    },
    {
        _id: 'quadrilateralWithRoundCorners',
        name: 'Rectangle/square with round corners',
        dimensions: 3,
        items: foldTypes
    }
];

interface SummaryPanelProps {
    spaShape: string;
    dimensions: Record<string, string>;
    foldType: string;
    foldDirection: string;
    sizeTaper: string;
    color: string;
    vapourBarrier: string;
    hingeInsulation: string;
    handles: string;
    speakerHoods: string;
    skirtLength: string;
    tieDowns: string;
    tieDownOptions: string;
    insidePullHandles: string;
    lifterPlates: string;
    notes: string;
    coverType: string;
    manufacturer: string;
    model: string;
    year: string;
  }

const SpaCoverSummary = () => {
    const token = localStorage.getItem('token');

    if (!token) {
        // Si no hay usuario, no mostrar el componente y redirigir al login
        return <Navigate to="/Login" replace />;
    }
    const [manufacturer, setManufacturer] = useState('');
    const [model, setModel] = useState('');
    const [year, setYear] = useState('');
    const [spaShape, setSpaShape] = useState('');
    const [foldType, setFoldType] = useState('');
    const [sizeTaper, setSizeTaper] = useState('');
    const [color, setColor] = useState('');
    const [notes, setNotes] = useState('');
    const [skirtLength, setSkirtLength] = useState('');
    const [tieDowns, setTieDowns] = useState('4');  // Valor por defecto: '4'
    const [tieDownOptions, setTieDownOptions] = useState('Standard');  // Valor por defecto: 'Standard'
    const [foldDirection, setFoldDirection] = useState('');
    const [vapourBarrier, setVapourBarrier] = useState('3mil');  // Valor por defecto: '3mil'
    const [hingeInsulation, setHingeInsulation] = useState('NONE');  // Valor por defecto: 'NONE'
    const [handles, setHandles] = useState('2 (STANDARD)');  // Valor por defecto: '2 (STANDARD)'
    const [speakerHoods, setSpeakerHoods] = useState('NONE');  // Valor por defecto: 'NONE'
    const [insidePullHandles, setInsidePullHandles] = useState('NONE');  // Valor por defecto: 'NONE'
    const [lifterPlates, setLifterPlates] = useState('NONE');
    const [coverType, setCoverType] = useState("Full Cover");
    const [loading, setLoading] = useState(false);
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
    const [foamPieces, setFoamPieces] = useState('');
    const [distanceFromCorner, setDistanceFromCorner] = useState('');
    const [lengthOfTie, setLengthOfTie] = useState('');
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    

    const [tagName, setTagName] = useState('');

    const [alert, setAlert] = useState<{
        open: boolean;
        message: string;
        success: boolean;
    }>({
        open: false,
        message: '',
        success: true
    });
    // Dimensiones dinámicas A-F
    const [dimensions, setDimensions] = useState<{ [key: string]: string }>({
        a: '',
        b: '',
        c: '',
        d: '',
        e: '',
        f: ''
    });

    const foldDirectionValue = foldDirection === "Fold A" ? 1 : 2;
    const selectedShape = StaticSpaShapes.find((shape) => shape._id === spaShape);

    // Evalúa si alguna dimensión está dentro de intervalo [min, max)
    const evalDimensionsInInterval = (min: number, max: number): boolean => {
        return Object.values(dimensions).some(val => {
            const num = Number(val);
            return num >= min && num < max;
        });
    };

    // Filtrar foldTypes válidos según dimensiones y shape
    const validFoldTypes = selectedShape?.items.filter(fold => {
        const id = fold._id;
        if (id === 1) { // Bi-Fold
            return !evalDimensionsInInterval(96, 200000);
        } else if (id === 2) { // Tri-Fold
            return evalDimensionsInInterval(96, 150);
        } else if (id === 3) { // Quad-Fold
            return evalDimensionsInInterval(96, 200);
        } else if (id === 4 || id === 5) { // Five/Six-Fold
            return evalDimensionsInInterval(150, 200000);
        }
        return false;
    });

    // Labels para dimensiones
    const dimensionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];

    const renderDimensionInputs = () => {
        const count = selectedShape?.dimensions || 0;
        return (
            <Box display="flex" gap={2} sx={{ mb: 2, pr: 2 }}>
                {dimensionLabels.slice(0, count).map((label) => (
                    <FormControl key={label} sx={{ flex: 1, minWidth: 0 }} error={!dimensions[label.toLowerCase()] || isNaN(Number(dimensions[label.toLowerCase()])) || Number(dimensions[label.toLowerCase()]) <= 0}>
                        <TextField
                            label={`Dimension ${label}`}
                            type="number"
                            value={dimensions[label.toLowerCase()]}
                            onChange={(e) =>
                                setDimensions(prev => ({ ...prev, [label.toLowerCase()]: e.target.value }))
                            }
                            required
                            error={!dimensions[label.toLowerCase()] || isNaN(Number(dimensions[label.toLowerCase()])) || Number(dimensions[label.toLowerCase()]) <= 0}
                            helperText={
                                !dimensions[label.toLowerCase()]
                                    ? 'Required'
                                    : isNaN(Number(dimensions[label.toLowerCase()]))
                                        ? 'Invalid number'
                                        : Number(dimensions[label.toLowerCase()]) <= 0
                                            ? 'Must be greater than 0'
                                            : ''
                            }
                        />
                    </FormControl>
                ))}
            </Box>
        );
    };



    // Cuando cambia spaShape reiniciamos dimensiones y foldType, foldDirection

    const handleSpaShapeChange = (e: SelectChangeEvent<string>) => {
        const newShape = e.target.value;
        setSpaShape(newShape);
        setFoldType('');
        setDimensions({ a: '', b: '', c: '', d: '', e: '', f: '' });

        const shape = StaticSpaShapes.find(s => s._id === newShape);
        if (shape?.dimensions === 1) {
            setFoldDirection('Fold A');
        } else {
            setFoldDirection('Fold B');
        }
    };

    // Aquí queda tu handleCreateOrder intacto o modifícalo para enviar dimensiones y foldDirection

    const handleCreateOrder = async () => {
        setLoading(true);
        setAlert({ open: false, message: '', success: true });

        const selectedShape=StaticSpaShapes.find(s => s._id === spaShape);
        const inputValue: InputValue = {
            'Order Type': coverType,
            'SO.': '',
            'PO.': purchaseOrderNumber,
            'Company': user ? (user.dealerName || user.name) : '',
            'Tag Name': tagName,
            'Make': manufacturer,
            'Model': model,
            'Shape': selectedShape ? selectedShape.name : '',
            'A': dimensions.a,
            'B': dimensions.b,
            'C': dimensions.c,
            'D': dimensions.d,
            'E': dimensions.e,
            'F': dimensions.f,
            'CUT SIDE': foldDirection,
            'TAPER': sizeTaper,
            'Skirt': skirtLength,
            'Wrap': vapourBarrier,
            'Pillow': hingeInsulation === 'FULL HINGE INSULATION' ? 'Full Pillow' : 'Standard',
            'Color': color,
            'Qty.': foamPieces
                ? (foamPieces === '1 piece of foam' ? '1' : '2')
                : '1',
            'Notes': notes
                + ' Tie down: ' + tieDowns
                + ' Tie Down Options: ' + tieDownOptions
                + (tieDownOptions === 'Custom'
                    ? `, Distance from corner: ${distanceFromCorner}, Length of Tie: ${lengthOfTie}`
                    : '')
        };

        try {
            const res = await fetch(
                'https://script.google.com/macros/s/AKfycbz02r4NTfi0JHEjc48mR7-iuxmaVs6MiJF084Y6s6YtvEtGRxq0mBw1nFBx84_zPtci/exec',
                {
                    redirect: 'follow',
                    method: 'POST',
                    body: JSON.stringify(inputValue),
                    headers: {
                        'Content-Type': 'text/plain;charset=utf-8'
                    }
                }
            );

            if (res.ok) {
                const data = await res.json();
                console.log('Order created successfully:', data);
                setAlert({
                    open: true,
                    message: 'Order created successfully.',
                    success: true
                });
                resetForm();
            } else {
                console.log('Error:', res);
                setAlert({
                    open: true,
                    message: 'The order could not be created.',
                    success: false
                });
                resetForm();
            }
        } catch (error) {
            console.error('Error:', error);
            setAlert({
                open: true,
                message: 'The order could not be created.',
                success: false
            });
        } finally {
            setLoading(false);
        }
    };




    const SummaryPanel = ({
        spaShape,
        dimensions,
        foldType,
        foldDirection,
        sizeTaper,
        color,
        vapourBarrier,
        hingeInsulation,
        handles,
        speakerHoods,
        skirtLength,
        tieDowns,
        tieDownOptions,
        insidePullHandles,
        lifterPlates,
        notes,
        coverType,
        manufacturer,
        model,
        year
    }: {
        spaShape: string;
        dimensions: Record<string, string>;
        foldType: string;
        foldDirection: string;
        sizeTaper: string;
        color: string;
        vapourBarrier: string;
        hingeInsulation: string;
        handles: string;
        speakerHoods: string;
        skirtLength: string;
        tieDowns: string;
        tieDownOptions: string;
        insidePullHandles: string;
        lifterPlates: string;
        notes: string;
        coverType: string;
        manufacturer: string;
        model: string;
        year: string;
    }) => {
        const selectedShape = StaticSpaShapes.find(s => s._id === spaShape);
        const dimensionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];

        const dimensionValues = dimensionLabels
            .slice(0, selectedShape?.dimensions || 0)
            .map(label => `${dimensions[label.toLowerCase()] || ''}"`)
            .join(' x ');

        const getFoldTypeName = () =>
            foldTypes.find(f => f._id.toString() === foldType)?.name || '';

        const getFoldTypePrice = () =>
            foldTypes.find(f => f._id.toString() === foldType)?.price || 0;

        const getFoamPiecesPrice = () =>
            foamPieces === '1 piece of foam' ? 5 :
                foamPieces === '2 pieces of foam' ? 10 : 0;

        const getCurrency = (value: number) => `$ ${value.toFixed(2)}`;

        // calcular total
        let total = 0;
        total += taperPrices[sizeTaper] || 0;

        if (coverType === "Foam Only") {
            if (vapourBarrier === '6mil') total += 15;
            total += getFoamPiecesPrice();
            if (lifterPlates === 'LIFTER PLATES') total += 50;
        } else if (coverType === "Skin Only") {
            if (skirtLength) total += skirtLengthPrices(skirtLength);
            if (tieDowns) total += tieDownPrices(tieDowns);
            if (hingeInsulation === 'FULL HINGE INSULATION') total += 20;
            if (handles === '4 HANDLES') total += 20;
            if (handles === '6 HANDLES') total += 30;
            if (speakerHoods === '2 SPEAKER HOODS') total += 100;
            if (speakerHoods === '4 SPEAKER HOODS') total += 200;
            if (insidePullHandles === 'INSIDE PULL HANDLERS') total += 10;
            // No sumar vapourBarrier ni lifterPlates en Skin Only
        } else {
            if (skirtLength) total += skirtLengthPrices(skirtLength);
            if (tieDowns) total += tieDownPrices(tieDowns);
            if (vapourBarrier === '6mil') total += 15;
            if (hingeInsulation === 'FULL HINGE INSULATION') total += 20;
            if (handles === '4 HANDLES') total += 20;
            if (handles === '6 HANDLES') total += 30;
            if (speakerHoods === '2 SPEAKER HOODS') total += 100;
            if (speakerHoods === '4 SPEAKER HOODS') total += 200;
            if (insidePullHandles === 'INSIDE PULL HANDLERS') total += 10;
            if (lifterPlates === 'LIFTER PLATES') total += 50;
        }

        if (coverType === "Skin Only") total += 10; // ejemplo: base Skin Only

        return (
            <Box sx={{ mt: 1, mx: 3, px: 2, border: '1px solid #ccc', borderRadius: 2 }}>
                <h3>Summary</h3>
                {coverType && (
                    <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>
                            <strong>Usage:</strong> {coverType}
                            {coverType === "Foam Only" && foamPieces && ` `}
                        </span>
                        <span style={{ color: 'green' }}>
                            {coverType === "Foam Only" && foamPieces
                                ? getCurrency(getFoamPiecesPrice())
                                : coverType === "Skin Only"
                                    ? getCurrency(10)
                                    : getCurrency(0)}
                        </span>
                    </p>
                )}

                {selectedShape && (
                    <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong>{selectedShape.name}</strong>
                    </p>
                )}
                {dimensionValues && (
                    <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong>Dimensions:</strong>
                        <span>{dimensionValues}</span>
                    </p>
                )}
                {foldDirection && (
                    <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong>Fold Direction:</strong>
                        <span>{foldDirection} in Half</span>
                    </p>
                )}
                {foldType && (
                    <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>
                            <strong>Fold Type:</strong> {getFoldTypeName()}
                        </span>
                        <span style={{ color: 'green' }}>{getCurrency(getFoldTypePrice())}</span>
                    </p>
                )}
                {sizeTaper && (
                    <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>
                            <strong>Taper:</strong> {sizeTaper.replace('-', '" - ')}"
                        </span>
                        <span style={{ color: 'green' }}>
                            {getCurrency(taperPrices[sizeTaper] || 0)}
                        </span>
                    </p>
                )}

                {/* Foam Only oculta estos */}
                {coverType !== "Foam Only" && (
                    <>
                        {color && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <strong>Color:</strong>
                                <span>{color}</span>
                            </p>
                        )}
                        {hingeInsulation && hingeInsulation !== 'NONE' && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>{hingeInsulation}</strong></span>
                                <span style={{ color: 'green' }}>{getCurrency(20)}</span>
                            </p>
                        )}
                        {handles && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>Handles:</strong> {handles}</span>
                                <span style={{ color: 'green' }}>
                                    {getCurrency(handles === '4 HANDLES' ? 20 : handles === '6 HANDLES' ? 30 : 0)}
                                </span>
                            </p>
                        )}
                        {speakerHoods && speakerHoods !== 'NONE' && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>{speakerHoods}</strong></span>
                                <span style={{ color: 'green' }}>
                                    {getCurrency(speakerHoods === '4 SPEAKER HOODS' ? 200 : 100)}
                                </span>
                            </p>
                        )}
                        {skirtLength && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>Skirt Length:</strong> {skirtLength}"</span>
                                <span style={{ color: 'green' }}>
                                    {getCurrency(skirtLengthPrices(skirtLength))}
                                </span>
                            </p>
                        )}
                        {tieDowns && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>Tie Downs:</strong> {tieDowns} {tieDownOptions}</span>
                                <span style={{ color: 'green' }}>{getCurrency(tieDownPrices(tieDowns))}</span>
                            </p>
                        )}
                        {insidePullHandles === 'INSIDE PULL HANDLERS' && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <strong>Inside Pull Handles</strong>
                                <span style={{ color: 'green' }}>{getCurrency(10)}</span>
                            </p>
                        )}
                    </>
                )}

                {/* VapourBarrier y LifterPlates se ocultan en Skin Only */}
                {coverType !== "Skin Only" && (
                    <>
                        {vapourBarrier && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>Vapour Barrier:</strong> {vapourBarrier}</span>
                                <span style={{ color: 'green' }}>
                                    {getCurrency(vapourBarrier === '6mil' ? 15 : 0)}
                                </span>
                            </p>
                        )}
                        {lifterPlates === 'LIFTER PLATES' && (
                            <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <strong>Lifter Plates</strong>
                                <span style={{ color: 'green' }}>{getCurrency(50)}</span>
                            </p>
                        )}
                    </>
                )}

                {/* Total */}
                <p style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: '1rem' }}>
                    <span>Total:</span>
                    <span>{getCurrency(total)}</span>
                </p>
            </Box>
        );
    };


    const isFormValid = () => {
        return (
            purchaseOrderNumber.trim() !== '' &&
            tagName.trim() !== '' &&
            coverType !== '' &&
            sizeTaper !== '' &&
            spaShape !== '' &&
            foldDirection !== '' &&
            foldType !== '' &&
            (coverType === "Foam Only" || color !== '') &&
            (coverType === "Foam Only" || hingeInsulation !== '') &&
            (coverType === "Foam Only" || handles !== '') &&
            (coverType === "Foam Only" || speakerHoods !== '') &&
            (coverType === "Foam Only" || skirtLength !== '') &&
            (coverType === "Foam Only" || tieDowns !== '') &&
            (coverType === "Foam Only" || tieDownOptions !== '') &&
            (coverType === "Foam Only" || insidePullHandles !== '') &&
            (coverType === "Skin Only" || vapourBarrier !== '') &&
            (coverType === "Skin Only" || lifterPlates !== '')
        );
    };

    const resetFoamOnlyForm = () => {
        setManufacturer('');
        setModel('');
        setYear('');
        setSpaShape('');
        setFoldType('');
        setSizeTaper('');
        setColor('');             // <- ahora vacío
        setNotes('');
        setSkirtLength('');       // <- ahora vacío
        setTieDowns('');          // <- ahora vacío
        setTieDownOptions('');    // <- ahora vacío
        setFoldDirection('');
        setVapourBarrier('3mil'); // mantiene default
        setHingeInsulation('');   // <- ahora vacío
        setHandles('');           // <- ahora vacío
        setSpeakerHoods('');      // <- ahora vacío
        setInsidePullHandles(''); // <- ahora vacío
        setLifterPlates('NONE');
        setPurchaseOrderNumber('');
        setDistanceFromCorner('');
        setLengthOfTie('');
      };

    const resetForm = () => {
        setManufacturer('');
        setModel('');
        setYear('');
        setSpaShape('');
        setFoldType('');
        setSizeTaper('');
        setColor('');
        setNotes('');
        setSkirtLength('');
        setTieDowns('4');             // valor por defecto
        setTieDownOptions('Standard'); // valor por defecto
        setFoldDirection('');
        setVapourBarrier('3mil');     // valor por defecto
        setHingeInsulation('NONE');   // valor por defecto
        setHandles('2 (STANDARD)');   // valor por defecto
        setSpeakerHoods('NONE');      // valor por defecto
        setInsidePullHandles('NONE'); // valor por defecto
        setLifterPlates('NONE');
        setPurchaseOrderNumber('');
        setFoamPieces('');
        setDistanceFromCorner('');
        setLengthOfTie('');
        setTagName('');
      };

console.log('spaShape:', spaShape, 'foldDirection:', foldDirection, typeof foldDirection);



    return (
        <>
            {/* Loader + Modal */}
            {loading && (
                <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            <Snackbar
                open={alert.open}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // 👈 parte inferior y centrado
                autoHideDuration={null} // 👈 sin temporizador
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.success ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>

            <Stack minHeight="60vh" alignItems="center" spacing={5}>
                <FixNavbarSpace />
                <Container>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} md={4} style={{ textAlign: 'center' }}>

                            <FormControl fullWidth sx={{ mb: 2, pr: 2, pt: 4 }}>
                                <ButtonGroup variant="outlined" fullWidth>
                                    <Button
                                        variant={coverType === "Full Cover" ? "contained" : "outlined"}
                                        onClick={() => {
                                            setCoverType("Full Cover");
                                            setFoamPieces("1 piece of foam"); resetForm();// resetear foamPieces
                                          }}
                                    >
                                        FULL COVER
                                    </Button>

                                    <Button
                                        variant={coverType === "Foam Only" ? "contained" : "outlined"}
                                        onClick={() => {
                                            setCoverType("Foam Only"); setFoamPieces("1 piece of foam"); resetFoamOnlyForm();
                                        
                                            // limpiar valores relacionados
                                            setColor('');
                                            setHingeInsulation(''); // o '' si prefieres
                                            setHandles('');
                                            setSpeakerHoods('');
                                            setSkirtLength('');
                                            setTieDowns('');
                                            setTieDownOptions('');
                                            setInsidePullHandles('');
                                          }}
                                    >
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            FOAM ONLY
                                            <span
                                                style={{
                                                    color: coverType === "Foam Only" ? 'white' : 'green',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                + $5.00
                                            </span>
                                        </Box>
                                    </Button>

                                    <Button
                                        variant={coverType === "Skin Only" ? "contained" : "outlined"}
                                        onClick={() => {
                                            setCoverType("Skin Only");
                                            setFoamPieces("1 piece of foam"); resetForm();// resetear foamPieces
                                          }}
                                    >
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            SKIN ONLY
                                            <span
                                                style={{
                                                    color: coverType === "Skin Only" ? 'white' : 'green',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                + $10.00
                                            </span>
                                        </Box>
                                    </Button>
                                </ButtonGroup>
                            </FormControl>

                            <FormControl fullWidth sx={{ mb: 2, pr: 2 }}>
                                <TextField
                                    required
                                    label="Purchase Order Number"
                                    value={purchaseOrderNumber}
                                    onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                                    error={!purchaseOrderNumber}
                                    helperText={!purchaseOrderNumber ? 'This field is required' : ''}
                                />
                            </FormControl>

                            <FormControl fullWidth sx={{ mb: 2, pr: 2 }}>
                                <TextField
                                    required
                                    label="Tag Name"
                                    value={tagName}
                                    onChange={(e) => setTagName(e.target.value)}
                                    error={!tagName}
                                    helperText={!tagName ? 'This field is required' : ''}
                                />
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2, pr: 2 }} error={!sizeTaper}>
                                <InputLabel required>Taper</InputLabel>
                                <Select
                                    value={sizeTaper}
                                    label="Taper"
                                    onChange={(e) => setSizeTaper(e.target.value)}
                                >
                                    {Object.entries(taperPrices).map(([value, price]) => (
                                        <MenuItem key={value} value={value}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{value.replace('-', '" - ')}"</span>
                                                <span style={{ color: 'green' }}>$ {price}</span>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {!sizeTaper && <FormHelperText>This field is required</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2, pr: 2 }} error={!spaShape}>
                                <InputLabel required>Spa Shape</InputLabel>
                                <Select
                                    value={spaShape}
                                    label="Spa Shape"
                                    onChange={handleSpaShapeChange}
                                >
                                    {StaticSpaShapes.map((shape) => (
                                        <MenuItem key={shape._id} value={shape._id}>
                                            {shape.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {!spaShape && <FormHelperText>This field is required</FormHelperText>}
                            </FormControl>

                            {spaShape ? (
                                <>
                                    {spaShape === 'rectangleWith2CutCorners' && (
                                        <Rectangle2CutCorners
                                            a={dimensions.a}
                                            b={dimensions.b}
                                            c={dimensions.c}
                                            d={dimensions.d}
                                            e={dimensions.e}
                                            f={dimensions.f}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    {spaShape === 'rectangleWith4CutCorners' && (
                                        <Rectangle4CutCorners
                                            a={dimensions.a}
                                            b={dimensions.b}
                                            c={dimensions.c}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    {spaShape === 'oneCutCorner' && (
                                        <OneCutCorner
                                            a={dimensions.a}
                                            b={dimensions.b}
                                            c={dimensions.c}
                                            d={dimensions.d}
                                            e={dimensions.e}
                                            f={dimensions.f}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    {spaShape === 'octagon' && (
                                        <Octagon
                                            a={dimensions.a}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    {spaShape === 'quadrilateral' && (
                                        <Quadrilateral
                                            a={dimensions.a}
                                            b={dimensions.b}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    {spaShape === 'circle' && (
                                        <CircleShape
                                            a={dimensions.a}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    {spaShape === 'quadrilateralWithRoundCorners' && (
                                        <QuadrilateralWithRoundCorners
                                            a={dimensions.a}
                                            b={dimensions.b}
                                            c={dimensions.c}
                                            foldDirection={foldDirectionValue}
                                            foldType={foldType}
                                        />
                                    )}

                                    <p className="grey--text">Reference</p>
                                </>
                            ) : null}



                            {/* Inputs dinámicos para dimensiones */}
                            {renderDimensionInputs()}


                             {/* Fold Type filtrado */}
                             <FormControl fullWidth sx={{ mb: 2, pr: 2 }} error={foldType === '' && selectedShape != null}>
                                <InputLabel required>Fold Type</InputLabel>
                                <Select
                                    value={foldType}
                                    label="Fold Type"
                                    onChange={(e) => setFoldType(e.target.value)}
                                    disabled={!selectedShape} // Deshabilitado si no se selecciona la forma
                                >
                                    {validFoldTypes?.map((fold) => (
                                        <MenuItem key={fold._id} value={fold.name}>
                                            {fold.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {/* Solo mostramos el texto de error si no está deshabilitado */}
                                {foldType === '' && selectedShape && <FormHelperText error>This field is required</FormHelperText>}
                            </FormControl>



                            {/* Fold Direction si dimensiones >= 2 */}
                            {(selectedShape?.dimensions || 0) >= 2 && (
                                <FormControl fullWidth sx={{ mb: 2, pr: 2 }} error={!foldDirection}>
                                    <InputLabel required>Fold Direction</InputLabel>
                                    <Select
                                        value={foldDirection}
                                        label="Fold Direction"
                                        onChange={(e) => setFoldDirection(e.target.value)}
                                    >
                                        <MenuItem value="Fold A">Fold A</MenuItem>
                                        <MenuItem value="Fold B">Fold B</MenuItem>
                                    </Select>
                                    {!foldDirection && <FormHelperText>This field is required</FormHelperText>}
                                </FormControl>

                            )}

                           



                            {coverType !== "Foam Only" && (
                                <FormControl
                                    fullWidth
                                    sx={{ mb: 2, pr: 2 }}
                                    error={coverType !== "Foam Only" && !color}
                                >
                                    <InputLabel required={coverType !== "Foam Only"}>Color</InputLabel>
                                    <Select
                                        value={color}
                                        label="Color"
                                        onChange={(e) => setColor(e.target.value)}
                                        required={coverType !== "Foam Only"}
                                    >
                                        <MenuItem value="Charcoal">Charcoal</MenuItem>
                                        <MenuItem value="Light Grey">Light Grey</MenuItem>
                                        <MenuItem value="Dark Grey">Dark Grey</MenuItem>
                                        <MenuItem value="Chocolate">Chocolate</MenuItem>
                                        <MenuItem value="Chesnut">Chesnut</MenuItem>
                                        <MenuItem value="Black (weather-tech)">Black (weather-tech)</MenuItem>
                                        <MenuItem value="Black">Black</MenuItem>
                                        <MenuItem value="Sunbrella">Sunbrella</MenuItem>
                                    </Select>
                                    {coverType !== "Foam Only" && !color && (
                                        <FormHelperText>This field is required</FormHelperText>
                                    )}
                                </FormControl>
                            )}



                            {coverType !== "Foam Only" && (
                                <FormControl
                                    fullWidth
                                    sx={{ mb: 2, pr: 2 }}
                                    error={coverType !== "Foam Only" && !skirtLength}
                                >
                                    <InputLabel required={coverType !== "Foam Only"}>Skirt Length</InputLabel>
                                    <Select
                                        value={skirtLength}
                                        onChange={(e) => setSkirtLength(e.target.value)}
                                        label="Skirt Length"
                                        required={coverType !== "Foam Only"}
                                    >
                                        {['0', '1', '2', '2.5', '3', '3.25', '3.50', '4', '4.5', '5', '5.25', '6', '6.5', '7', '7.5'].map((length) => (
                                            <MenuItem key={length} value={length}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                    <span>{length}</span>
                                                    {skirtLengthPrices(length) > 0 && (
                                                        <span style={{ color: 'green' }}>
                                                            $ {skirtLengthPrices(length)}
                                                        </span>
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {coverType !== "Foam Only" && !skirtLength && (
                                        <FormHelperText>This field is required</FormHelperText>
                                    )}
                                </FormControl>
                            )}

                            <Box display="flex" gap={2} sx={{ mb: 2, pr: 2 }}>
                                <FormControl fullWidth>
                                    <TextField
                                       
                                        label="Manufacturer"
                                        value={manufacturer}
                                        onChange={(e) => setManufacturer(e.target.value)}
                                      
                                        
                                    />
                                </FormControl>

                                <FormControl fullWidth>
                                    <TextField
                                       
                                        label="Model"
                                        value={model}
                                        onChange={(e) => setModel(e.target.value)}
                                        
                                        
                                    />
                                </FormControl>


                            </Box>
                            <FormControl fullWidth sx={{ mb: 2, pr: 2 }}>
                                <TextField
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </FormControl>
                            <Box display="flex" gap={2} sx={{ mb: 2, pr: 2 }}>



                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} style={{ textAlign: 'center' }}>

                            {coverType === "Foam Only" && (
                                <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2, pt: 4 }}>
                                    <ButtonGroup orientation="vertical">
                                        {['1 piece of foam', '2 pieces of foam'].map(option => (
                                            <Button
                                                key={option}
                                                variant={foamPieces === option ? 'contained' : 'outlined'}
                                                onClick={() => setFoamPieces(option)}
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <span>{option}</span>
                                                    <span
                                                        style={{
                                                            color: foamPieces === option ? 'white' : 'green',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        {option === '1 piece of foam' ? '+ $5.00' : '+ $10.00'}
                                                    </span>
                                                </Box>
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                </FormControl>
                            )}

                            {coverType !== "Foam Only" && (
                                <>
                                    <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2, pt: 4 }}>
                                        <ButtonGroup orientation="vertical" aria-label="Tie Downs">
                                            {['4', '6', '8'].map((label) => {
                                                const price = tieDownPrices(label);
                                                const priceText = price > 0 ? `+ $${price}` : '';

                                                return (
                                                    <Button
                                                        key={label}
                                                        variant={tieDowns === label ? 'contained' : 'outlined'}
                                                        onClick={() => setTieDowns(label)}
                                                    >
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <span>{label} TIE DOWNS</span>
                                                            {priceText && (
                                                                <span
                                                                    style={{
                                                                        color: tieDowns === label ? 'white' : 'green',
                                                                        fontSize: '0.8rem'
                                                                    }}
                                                                >
                                                                    {priceText}
                                                                </span>
                                                            )}
                                                        </Box>
                                                    </Button>
                                                );
                                            })}
                                        </ButtonGroup>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2 }}>
                                        <ButtonGroup orientation="vertical" aria-label="Tie Down Options">
                                            {['Standard', 'Custom'].map((label) => (
                                                <Button
                                                    key={label}
                                                    variant={tieDownOptions === label ? 'contained' : 'outlined'}
                                                    onClick={() => setTieDownOptions(label)}
                                                >
                                                    {label} TIE DOWN
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </FormControl>

                                    {tieDownOptions === "Custom" && (
                                        <Box display="flex" gap={2} sx={{ mb: 2, ml: 2 }}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Distance from corner"
                                                    type="number"
                                                    value={distanceFromCorner}
                                                    onChange={(e) => setDistanceFromCorner(e.target.value)}
                                                    required
                                                    helperText="Inches from corner of cover to center of strap"
                                                />
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Length of Tie"
                                                    type="number"
                                                    value={lengthOfTie}
                                                    onChange={(e) => setLengthOfTie(e.target.value)}
                                                    required
                                                    helperText="Inches from underside cover, including skirt and clip"
                                                />
                                            </FormControl>
                                        </Box>
                                    )}

                                    <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2 }}>
                                        <ButtonGroup orientation="vertical" aria-label="Hinge Insulation">
                                            {['NONE', 'FULL HINGE INSULATION'].map((label) => (
                                                <Button
                                                    key={label}
                                                    variant={hingeInsulation === label ? 'contained' : 'outlined'}
                                                    onClick={() => setHingeInsulation(label)}
                                                >
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <span>{label}</span>
                                                        {label === 'FULL HINGE INSULATION' && (
                                                            <span
                                                                style={{
                                                                    color: hingeInsulation === label ? 'white' : 'green',
                                                                    fontSize: '0.8rem'
                                                                }}
                                                            >
                                                                + $20
                                                            </span>
                                                        )}
                                                    </Box>
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2 }}>
                                        <ButtonGroup orientation="vertical" aria-label="Handles (Extra)">
                                            {['2 (STANDARD)', '4 HANDLES', '6 HANDLES'].map((label) => {
                                                let priceText = '';
                                                if (label === '4 HANDLES') priceText = '+ $20';
                                                if (label === '6 HANDLES') priceText = '+ $30';

                                                return (
                                                    <Button
                                                        key={label}
                                                        variant={handles === label ? 'contained' : 'outlined'}
                                                        onClick={() => setHandles(label)}
                                                    >
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <span>{label}</span>
                                                            {priceText && (
                                                                <span
                                                                    style={{
                                                                        color: handles === label ? 'white' : 'green',
                                                                        fontSize: '0.8rem'
                                                                    }}
                                                                >
                                                                    {priceText}
                                                                </span>
                                                            )}
                                                        </Box>
                                                    </Button>
                                                );
                                            })}
                                        </ButtonGroup>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2 }}>
                                        <ButtonGroup orientation="vertical" aria-label="Speaker Hoods">
                                            {['NONE', '2 SPEAKER HOODS', '4 SPEAKER HOODS'].map((label) => {
                                                let priceText = '';
                                                if (label === '2 SPEAKER HOODS') priceText = '+ $100';
                                                if (label === '4 SPEAKER HOODS') priceText = '+ $200';

                                                return (
                                                    <Button
                                                        key={label}
                                                        variant={speakerHoods === label ? 'contained' : 'outlined'}
                                                        onClick={() => setSpeakerHoods(label)}
                                                    >
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <span>{label}</span>
                                                            {priceText && (
                                                                <span
                                                                    style={{
                                                                        color: speakerHoods === label ? 'white' : 'green',
                                                                        fontSize: '0.8rem'
                                                                    }}
                                                                >
                                                                    {priceText}
                                                                </span>
                                                            )}
                                                        </Box>
                                                    </Button>
                                                );
                                            })}
                                        </ButtonGroup>
                                    </FormControl>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            '& > *': { m: 1 }
                                        }}
                                    >
                                        <FormControl fullWidth sx={{ mb: 2, ml: 2 }}>
                                            <ButtonGroup orientation="vertical" aria-label="Inside Pull Handles">
                                                {['NONE', 'INSIDE PULL HANDLERS'].map((label) => {
                                                    const priceText = label === 'INSIDE PULL HANDLERS' ? '+ $10' : '';

                                                    return (
                                                        <Button
                                                            key={label}
                                                            variant={insidePullHandles === label ? 'contained' : 'outlined'}
                                                            onClick={() => setInsidePullHandles(label)}
                                                        >
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <span>{label}</span>
                                                                {priceText && (
                                                                    <span
                                                                        style={{
                                                                            color: insidePullHandles === label ? 'white' : 'green',
                                                                            fontSize: '0.8rem'
                                                                        }}
                                                                    >
                                                                        {priceText}
                                                                    </span>
                                                                )}
                                                            </Box>
                                                        </Button>
                                                    );
                                                })}
                                            </ButtonGroup>
                                        </FormControl>
                                    </Box>
                                </>
                            )}

                            {coverType !== "Skin Only" && (
                                <>
                                    <FormControl fullWidth sx={{ mb: 2, ml: 2, pr: 2 }}>
                                        <ButtonGroup orientation="vertical" aria-label="Vapour Barrier">
                                            <Button
                                                variant={vapourBarrier === '3mil' ? 'contained' : 'outlined'}
                                                onClick={() => setVapourBarrier('3mil')}
                                            >
                                                VAPOUR BARRIER 3 MIL (STANDARD)
                                            </Button>
                                            <Button
                                                variant={vapourBarrier === '6mil' ? 'contained' : 'outlined'}
                                                onClick={() => setVapourBarrier('6mil')}
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <span>VAPOUR BARRIER 6 MIL</span>
                                                    <span
                                                        style={{
                                                            color: vapourBarrier === '6mil' ? 'white' : 'green',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        + $15.00
                                                    </span>
                                                </Box>
                                            </Button>
                                        </ButtonGroup>
                                    </FormControl>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            '& > *': { m: 1 }
                                        }}
                                    >
                                        <FormControl fullWidth sx={{ mb: 2, ml: 2 }}>
                                            <ButtonGroup orientation="vertical" aria-label="Lifter Plates">
                                                {['NONE', 'LIFTER PLATES'].map((label) => {
                                                    const priceText = label === 'LIFTER PLATES' ? '+ $50' : '';

                                                    return (
                                                        <Button
                                                            key={label}
                                                            variant={lifterPlates === label ? 'contained' : 'outlined'}
                                                            onClick={() => setLifterPlates(label)}
                                                        >
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <span>{label}</span>
                                                                {priceText && (
                                                                    <span
                                                                        style={{
                                                                            color: lifterPlates === label ? 'white' : 'green',
                                                                            fontSize: '0.8rem'
                                                                        }}
                                                                    >
                                                                        {priceText}
                                                                    </span>
                                                                )}
                                                            </Box>
                                                        </Button>
                                                    );
                                                })}
                                            </ButtonGroup>
                                        </FormControl>
                                    </Box>
                                </>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4} style={{ textAlign: 'center' }}>
                            <Box sx={{ textAlign: 'center', mt: 2, pt: 1 }}>
                                <SummaryPanel
                                    spaShape={spaShape}
                                    dimensions={dimensions}
                                    foldType={foldType}
                                    foldDirection={foldDirection}
                                    sizeTaper={sizeTaper}
                                    color={color}
                                    vapourBarrier={vapourBarrier}
                                    hingeInsulation={hingeInsulation}
                                    handles={handles}
                                    speakerHoods={speakerHoods}
                                    skirtLength={skirtLength}
                                    tieDowns={tieDowns}
                                    tieDownOptions={tieDownOptions}
                                    insidePullHandles={insidePullHandles}
                                    lifterPlates={lifterPlates}
                                    notes={notes}
                                    coverType={coverType}
                                    manufacturer={manufacturer}
                                    model={model}
                                    year={year}
                                />

                            </Box>
                            <Box sx={{ textAlign: 'center', my: 4 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleCreateOrder}
                                    disabled={!isFormValid()}
                                >
                                    Create order
                                </Button>

                            </Box>
                        </Grid>
                    </Grid>

                </Container>
            </Stack>
        </>
    );
};

export default SpaCoverSummary;
